var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { parseIsoDate, toIsoDateString } from "./isoDate";
export var NO_PARAMETER = "none";
var PARAMETER_KEYS_WITH_FALLBACK = ["volume", "issue", "date", "period"];
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export var isIsoDate = function (value) {
    return !!value &&
        ISO_DATE.test(value) &&
        !Number.isNaN(parseIsoDate(value).getTime());
};
export var formatVolumeDate = function (iso) {
    var _a = iso.split("-").map(Number), year = _a[0], month = _a[1], day = _a[2];
    return "".concat(day, ". ").concat(month, ". ").concat(year);
};
export var formatVolumeWindow = function (window) {
    return "".concat(formatVolumeDate(window.startDate), " \u2013 ").concat(formatVolumeDate(window.endDate));
};
export var isCalendarWindow = function (window) {
    var _a = window.startDate
        .split("-")
        .map(Number), startYear = _a[0], startMonth = _a[1], startDay = _a[2];
    var _b = window.endDate.split("-").map(Number), endYear = _b[0], endMonth = _b[1], endDay = _b[2];
    return (startYear === endYear &&
        startMonth === 1 &&
        startDay === 1 &&
        endMonth === 12 &&
        endDay === 31);
};
export var describeVolume = function (volume) {
    return isCalendarWindow(volume)
        ? volume.label
        : "".concat(volume.label, " (").concat(formatVolumeWindow(volume), ")");
};
// lists and section headings show the bare label; the window belongs to the volume's detail only
export var volumeOptionLabel = function (volume, formatMessage) {
    return volume.current
        ? formatMessage({ id: "ui-br-legaldeposits.periodicals.volume.option.current" }, { volume: volume.label })
        : volume.label;
};
export var containsDate = function (window, iso) {
    return window.startDate <= iso && iso <= window.endDate;
};
export var sortVolumesByStart = function (volumes) {
    return __spreadArray([], volumes, true).sort(function (a, b) {
        return a.startDate.localeCompare(b.startDate) ||
            a.endDate.localeCompare(b.endDate);
    });
};
export var findCurrentVolume = function (volumes) { return volumes.find(function (volume) { return volume.current; }); };
export var isVolumeOver = function (window, today) {
    if (today === void 0) { today = toIsoDateString(new Date()); }
    return window.endDate < today;
};
export var nextDay = function (iso) {
    var date = parseIsoDate(iso);
    date.setDate(date.getDate() + 1);
    return toIsoDateString(date);
};
export var windowsTouch = function (earlier, later) {
    return nextDay(earlier.endDate) === later.startDate;
};
// the backend refuses a double issue across a gap between volumes (DIFFERENT_VOLUMES)
export var adjacentVolumes = function (volumes, volumeId) {
    var sorted = sortVolumesByStart(volumes);
    var index = sorted.findIndex(function (volume) { return volume.id === volumeId; });
    if (index < 0) {
        return { previous: null, next: null };
    }
    var current = sorted[index];
    var previous = index > 0 ? sorted[index - 1] : null;
    var next = index < sorted.length - 1 ? sorted[index + 1] : null;
    return {
        previous: previous && windowsTouch(previous, current) ? previous : null,
        next: next && windowsTouch(current, next) ? next : null,
    };
};
export var monthKeyOf = function (iso) {
    var _a = iso.split("-").map(Number), year = _a[0], month = _a[1];
    return { key: "".concat(year, "-").concat(month), year: year, month: month };
};
// the backend sends "label:start:end" and neither labels nor ISO dates contain a colon
export var parseVolumeParameter = function (value) {
    var parts = value.split(":");
    if (parts.length !== 3 || !isIsoDate(parts[1]) || !isIsoDate(parts[2])) {
        return null;
    }
    return { label: parts[0], startDate: parts[1], endDate: parts[2] };
};
var describeParameter = function (key, value) {
    if (key === "volume") {
        var parsed = parseVolumeParameter(value);
        return parsed ? "".concat(parsed.label, " (").concat(formatVolumeWindow(parsed), ")") : value;
    }
    if (key === "date" && isIsoDate(value)) {
        return formatVolumeDate(value);
    }
    if (key === "period") {
        var _a = value.split(":"), start = _a[0], end = _a[1];
        return isIsoDate(start) && isIsoDate(end)
            ? "".concat(formatVolumeDate(start), " \u2013 ").concat(formatVolumeDate(end))
            : value;
    }
    return value;
};
// NO_PARAMETER lets a message fall back to a generic wording through an ICU select
export var errorParameterValues = function (parameters) {
    var grouped = new Map();
    (parameters !== null && parameters !== void 0 ? parameters : []).forEach(function (parameter) {
        var _a;
        if (!parameter.key || !parameter.value)
            return;
        var list = (_a = grouped.get(parameter.key)) !== null && _a !== void 0 ? _a : [];
        list.push(describeParameter(parameter.key, parameter.value));
        grouped.set(parameter.key, list);
    });
    var values = {};
    PARAMETER_KEYS_WITH_FALLBACK.forEach(function (key) {
        values[key] = NO_PARAMETER;
    });
    grouped.forEach(function (list, key) {
        values[key] = list.join(", ");
    });
    return values;
};
var MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
// 29. 2. refused like the backend: a volume must start on a day every year has
export var isValidVolumeAnchor = function (day, month) {
    return Number.isInteger(day) &&
        Number.isInteger(month) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= MONTH_LENGTHS[month - 1];
};
// a start of 29. 2. cannot become the title's anchor, the backend refuses it
export var anchorOfStart = function (startDate) {
    if (!isIsoDate(startDate))
        return null;
    var _a = startDate.split("-").map(Number), month = _a[1], day = _a[2];
    return isValidVolumeAnchor(day, month) ? { day: day, month: month } : null;
};
export var formatVolumeAnchor = function (formatMessage, day, month) {
    return formatMessage({ id: "ui-br-legaldeposits.periodicals.volume.anchorValue" }, {
        day: String(day),
        month: formatMessage({
            id: "ui-br-legaldeposits.periodicals.volume.anchorMonth.".concat(month),
        }),
    });
};
